import { useState, useEffect } from 'react';
import GenesisAccountCreator from '../crm/GenesisAccountCreator';
import { useTenant } from '../../context/TenantContext'; 
import { getTenantLeads } from '../../services/crmService'; 

export default function ApexGenesisForm() {
  const [showIntake, setShowIntake] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  
  const { tenantId, loading: tenantLoading } = useTenant(); 

  const [metrics, setMetrics] = useState({
    provisioned30Days: 0,
    pendingCount: 0,
    newMrr: 0
  });
  
  const [pendingDeals, setPendingDeals] = useState([]);

  // --- ⚡️ LIVE FIREBASE FETCH (MULTI-TENANT) ⚡️ ---
  useEffect(() => {
    const fetchGatewayData = async () => {
      // Do not attempt fetch if tenant is still resolving
      if (tenantLoading || !tenantId) return;

      setIsLoading(true);
      try {
        // Fetch leads from the strict tenant boundary
        const deals = await getTenantLeads(tenantId);

        const now = new Date();
        const thirtyDaysAgo = new Date(now.setDate(now.getDate() - 30));

        let provCount = 0;
        let mrrSum = 0;
        const pending = [];

        deals.forEach(deal => {
          const mrr = Number(deal.mrr_value || deal.mrr || deal.calculatedMrr || 0);
          // Use updatedAt if available, fallback to createdAt or current date
          const dealDate = deal.updatedAt?.toDate ? deal.updatedAt.toDate() : (deal.createdAt?.toDate ? deal.createdAt.toDate() : new Date());

          // 1. Calculate Closed/Provisioned in the last 30 days
          if (deal.stage === 'closedWon' && dealDate >= thirtyDaysAgo) {
            provCount++;
            mrrSum += mrr;
          }

          // 2. Identify Pending Invoices/Activations
          // Anything in the pipeline that hasn't closed, or hasn't had Auth provisioned
          if (deal.stage !== 'closedWon' && deal.authProvisioned === false) {
            pending.push({
              id: deal.id,
              dealerId: deal.dealerId || deal.id.substring(0, 8).toUpperCase(),
              companyName: deal.company_name || 'Unknown Entity',
              email: deal.contact_email || 'No email provided',
              mrr: mrr,
              date: dealDate,
              timeAgo: calculateTimeAgo(dealDate)
            });
          }
        });

        setMetrics({
          provisioned30Days: provCount,
          pendingCount: pending.length,
          newMrr: mrrSum
        });
        
        // Sort pending deals by newest first
        setPendingDeals(pending.sort((a, b) => b.date - a.date));

      } catch (error) {
        console.error("Failed to fetch Gateway data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchGatewayData();
  }, [showIntake, tenantId, tenantLoading]); // Re-run the sweep whenever the intake modal closes or tenant changes

  // Helper function for "Time Ago" display
  const calculateTimeAgo = (date) => {
    const seconds = Math.floor((new Date() - date) / 1000);
    let interval = seconds / 86400;
    if (interval > 1) return Math.floor(interval) + " days ago";
    interval = seconds / 3600;
    if (interval > 1) return Math.floor(interval) + " hrs ago";
    interval = seconds / 60;
    if (interval > 1) return Math.floor(interval) + " mins ago";
    return "Just now";
  };

  return (
    <div className="animate-in fade-in duration-500 pb-12 relative">
      {/* Header */}
      <div className="flex justify-between items-end mb-6 border-b border-slate-300 pb-4">
        <div>
          <h1 className="text-2xl font-black text-slate-900 tracking-tight uppercase">Genesis Gateway</h1>
          <p className="text-slate-500 text-sm mt-1 font-medium">Master control for tenant provisioning and invoice generation.</p>
        </div>
        <button 
          onClick={() => setShowIntake(true)}
          className="bg-emerald-600 hover:bg-emerald-700 text-white text-[10px] font-bold py-2 px-6 transition-colors uppercase tracking-widest border border-emerald-600 shadow-sm"
        >
          + Provision New Account
        </button>
      </div>

      {/* High-Level Provisioning Stats */}
      <div className="grid grid-cols-3 gap-0 border border-slate-300 mb-6 bg-white shadow-sm">
        <div className="p-4 border-r border-slate-200">
          <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-1">Accounts Provisioned (30 Days)</p>
          <p className="text-2xl font-black text-slate-800">
            {isLoading || tenantLoading ? '-' : metrics.provisioned30Days}
          </p>
        </div>
        <div className="p-4 border-r border-slate-200 bg-amber-50/30">
          <p className="text-[10px] font-bold text-amber-700 uppercase tracking-widest mb-1">Pending Invoices</p>
          <p className="text-2xl font-black text-amber-700">
            {isLoading || tenantLoading ? '-' : metrics.pendingCount}
          </p>
        </div>
        <div className="p-4">
          <p className="text-[10px] font-bold text-slate-500 uppercase tracking-widest mb-1">New MRR Captured</p>
          <p className="text-2xl font-black text-emerald-700">
            {isLoading || tenantLoading ? '...' : `$${metrics.newMrr.toLocaleString()}`}
          </p>
        </div>
      </div>

      {/* Pending Activations Ledger */}
      <div className="bg-white border border-slate-300 shadow-sm">
        <div className="bg-slate-100 border-b border-slate-300 px-4 py-3">
          <h3 className="text-[10px] font-bold text-slate-600 uppercase tracking-widest">Awaiting Payment Activation</h3>
        </div>
        
        <div className="divide-y divide-slate-200">
          
          {(isLoading || tenantLoading) && (
            <div className="p-12 text-center text-slate-500 text-xs font-bold uppercase tracking-widest">
               <div className="w-8 h-8 border-2 border-amber-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
               Scanning Provisioning Queue...
            </div>
          )}

          {!(isLoading || tenantLoading) && pendingDeals.length === 0 && (
            <div className="p-12 text-center text-slate-500 text-sm font-bold">
               No accounts currently pending activation.
            </div>
          )}

          {!(isLoading || tenantLoading) && pendingDeals.map(deal => (
            <div key={deal.id} className="px-4 py-3 grid grid-cols-12 gap-4 items-center hover:bg-slate-50 transition-colors">
              <div className="col-span-3">
                <span className="text-sm font-bold text-slate-900 block">{deal.companyName}</span>
                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">{deal.dealerId}</span>
              </div>
              <div className="col-span-3">
                <span className="text-xs font-bold text-slate-700 block mb-0.5">{deal.email}</span>
                <span className="text-[10px] text-slate-500 font-medium block">Intake Sent: {deal.timeAgo}</span>
              </div>
              <div className="col-span-2">
                <span className="border border-amber-500 text-amber-600 text-[10px] font-bold px-2 py-0.5 uppercase tracking-widest bg-amber-50">Awaiting Payment</span>
              </div>
              <div className="col-span-2 text-sm font-bold text-slate-700">
                ${deal.mrr.toLocaleString(undefined, {minimumFractionDigits: 2})} <span className="text-[9px] text-slate-400 uppercase tracking-widest">MRR</span>
              </div>
              <div className="col-span-2 text-right">
                <button 
                  onClick={() => alert(`Simulating resend of invoice link for ${deal.companyName}...`)}
                  className="text-[10px] font-bold text-slate-500 hover:text-emerald-600 uppercase tracking-widest transition-colors"
                >
                  Resend Link
                </button>
              </div>
            </div>
          ))}

        </div>
      </div>

      {/* THE FORM RENDER */}
      {showIntake && <GenesisAccountCreator onClose={() => setShowIntake(false)} />}
    </div>
  );
}